/**
 * Global swallower for noisy AbortError unhandled rejections from EZUIKit.
 *
 * React StrictMode's double-mount in dev causes the first effect's in-flight
 * fetches to be aborted on cleanup, surfacing as "abort fetch" rejection
 * spam in the dev overlay. The same pattern happens in production when
 * the dashboard navigates away from a CCTV tile while a `getLive` /
 * stream-bootstrap fetch is in flight.
 *
 * Verbatim port of `app/components/GlobalErrorSwallow.tsx` from the
 * reference project at `/Users/zhizheng/Desktop/hik/` (see "Don't regress"
 * in `services/console/cctv/CLAUDE.md` for the historical context).
 *
 * The handler is installed once at module-load time via the
 * `__cctvSwallowInstalled` window flag (HMR re-imports the module — without
 * the guard we'd attach a fresh listener on every hot-reload, leaking
 * memory). Mounted once at the app root from `AppLayout.tsx`; the
 * component itself renders nothing.
 */

if (typeof window !== 'undefined') {
  const w = window as Window & { __cctvSwallowInstalled?: boolean }

  if (!w.__cctvSwallowInstalled) {
    w.__cctvSwallowInstalled = true
    window.addEventListener('unhandledrejection', evt => {
      const r = evt.reason as { message?: string; name?: string } | string | null
      const msg = typeof r === 'string' ? r : (r?.message ?? '')

      if (
        msg === 'abort fetch' ||
        msg.includes('AbortError') ||
        msg.includes('The user aborted') ||
        (r && typeof r === 'object' && r.name === 'AbortError')
      ) {
        evt.preventDefault()
        // Also stop other listeners (the dev overlay) from running.
        evt.stopImmediatePropagation()
      }
    })
  }
}

export function GlobalErrorSwallow() {
  return null
}
